import React from "react";
import LocalizedLink from "../../../components/localized-link";
import { Box, styled, Theme, Typography, useTheme } from "@material-ui/core";
import Imgix from "react-imgix";
import { POD_KEYS_COUNT } from "../../activation/constants";
import ExpressDeliveryLogo from "../../express-delivery/ExpressDeliveryLogo";
import PodRating from "../PodRating";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import useIsMounted from "../../../hooks/use-is-mounted";
import { getProductVariantComingSoonLabel } from "../../../helpers/product";
import {
  ProductVariantListItemType,
  ProductVariantType
} from "../../../types/airgraft";

export const WrapperSkeleton = React.memo(
  styled("div")(({ theme }) => ({
    position: "relative",
    backgroundColor: theme.palette.grey[100],
    borderRadius: "3px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    border: `1px solid ${theme.palette.grey[300]}`,
    paddingBottom: "180px",
    [theme.breakpoints.down("xs")]: {
      paddingBottom: "152px"
    },
    // Shorter skelton when no price row
    "&.no-price": {
      paddingBottom: "126px",
      [theme.breakpoints.down("xs")]: {
        paddingBottom: "106px"
      }
    }
  }))
);

export const WrapperLink = styled(LocalizedLink)(({ theme }) => ({
  position: "relative",
  backgroundColor: theme.palette.grey[100],
  borderRadius: "3px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  transition: "background 100ms",
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
    textDecoration: "none"
  }
}));

export const CustomImgixImage = styled(Imgix)(({ theme }) => ({
  maxWidth: "100%",
  width: "100%",
  height: "auto",
  backgroundColor: theme.palette.grey[300],
  userSelect: "none",
  // Force 1:1 apect ratio when image is loading
  "&::before": {
    content: "",
    display: "block",
    paddingTop: "100%"
  }
}));

export const TitleTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontFamily: "Reckless, Georgia",
  letterSpacing: 0,
  fontSize: "2.4rem",
  lineHeight: "2.8rem",
  [theme.breakpoints.down("xs")]: {
    fontSize: "1.7rem",
    lineHeight: "2rem"
  }
}));

export const InfoTypography = styled(Typography)(
  ({ theme }: { theme: Theme; component?: React.ElementType }) => ({
    fontWeight: 400,
    letterSpacing: 0,
    fontSize: "1.4rem",
    lineHeight: "2rem",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.2rem",
      lineHeight: "1.6rem"
    }
  })
);

export const SecondaryInfoTypography = styled(Typography)(
  ({ theme }: { theme: Theme; component?: React.ElementType }) => ({
    fontWeight: 400,
    letterSpacing: 0,
    fontSize: "1.4rem",
    lineHeight: "2rem",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.2rem",
      lineHeight: "1.6rem"
    }
  })
);

export const PriceTypography = styled(Typography)(
  ({ theme }: { theme: Theme; component?: React.ElementType }) => ({
    whiteSpace: "nowrap",
    fontWeight: 500,
    fontFamily: "Reckless, Georgia",
    letterSpacing: 0,
    fontSize: "2.4rem",
    lineHeight: "2.4rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "2.3rem",
      lineHeight: "2.3rem"
    }
  })
);

export const CustomPodRating = styled(PodRating)(({ theme }) => ({
  [theme.breakpoints.down("xs")]: {
    "& span": {
      fontSize: "1.2rem",
      lineHeight: "1.2rem"
    }
  }
}));

export const WeightTypography = styled(Typography)(
  ({ theme }: { theme: Theme; component?: React.ElementType }) => ({
    fontFamily: "Roobert, Arial",
    fontSize: "2rem",
    lineHeight: "2.4rem",
    color: theme.palette.text.secondary,
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.6rem",
      lineHeight: "2rem"
    }
  })
);

// Base
export const AvailabilityLabel = styled(({ background, width, ...props }) => (
  <Box {...props} />
))(
  ({
    theme,
    width,
    background
  }: {
    theme: Theme;
    background?: string;
    width?: string;
  }) => ({
    position: "absolute",
    background: background || "#4F4E4D",
    height: "22px",
    top: "-22px",
    left: "-1px",
    minWidth: "100px",
    width: "auto",
    paddingLeft: "6px",
    paddingRight: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderTopRightRadius: "3px",
    fontSize: "1.1rem",
    lineHeight: "1.1rem",
    color: theme.palette.common.white,
    textTransform: "uppercase",
    fontFamily: "Roobert, Arial",
    fontWeight: 600,
    letterSpacing: "1px",
    "& .MuiSvgIcon-root": {
      color: theme.palette.common.white,
      fontSize: "1.6rem",
      marginRight: theme.spacing(0.25)
    },
    [theme.breakpoints.down("xs")]: {
      width: width || "86px",
      height: "20px",
      top: "-20px"
    }
  })
);

export const AvailableAvailabilityLabel = React.memo(() => {
  const theme = useTheme();
  return (
    <AvailabilityLabel background={theme.palette.expressDelivery} width="110px">
      <ExpressDeliveryLogo
        width="19px"
        variant="truck"
        style={{ marginRight: "4px" }}
      />
      Available
    </AvailabilityLabel>
  );
});

export const XpressAvailabilityLabel = React.memo(() => {
  const theme = useTheme();
  return (
    <AvailabilityLabel background={theme.palette.expressDelivery} width="90px">
      <ExpressDeliveryLogo width="70px" variant="white" />
    </AvailabilityLabel>
  );
});

export const ComingSoonAvailabilityLabel = React.memo(
  ({
    productVariant
  }: {
    productVariant: ProductVariantListItemType | ProductVariantType;
  }) => {
    const isMounted = useIsMounted();
    return (
      <AvailabilityLabel
        background="#676767"
        width="auto"
        paddingX={{ xs: "6px", md: "6px" }}
      >
        <Box
          marginRight={0.25}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CalendarTodayIcon fontSize="small" style={{ fontSize: "1.2rem" }} />
        </Box>
        {getProductVariantComingSoonLabel(productVariant, {
          format: "short",
          isSSR: !isMounted
        })}
      </AvailabilityLabel>
    );
  }
);

// "My Pods" components

export const ActionButton = styled(LocalizedLink)(
  ({ theme }: { theme: Theme; component?: React.ElementType }) => ({
    background: theme.palette.common.white,
    borderRadius: 0,
    width: "100%",
    height: "38px",
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    fontSize: "1.2rem",
    textTransform: "uppercase",
    textAlign: "center",
    fontWeight: 600,
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    letterSpacing: "1px",
    "&:hover": {
      background: theme.palette.grey[100]
    },
    [theme.breakpoints.up("md")]: {
      height: "44px"
    }
  })
);

export function SmallPodKeyInfo({ podKey }: { podKey: string }) {
  const theme = useTheme();
  return (
    <>
      <Box display="flex" flexDirection="column" alignItems="center">
        {/* Pod code */}
        <Typography
          component="div"
          style={{
            width: "51px",
            height: "32px",
            borderRadius: "6px",
            paddingLeft: "3px",
            letterSpacing: "2px",
            fontSize: "12px",
            lineHeight: "12px",
            fontFamily: "'Roboto Mono', monospace",
            border: `1px solid ${theme.palette.common.black}`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
            userSelect: "none"
          }}
        >
          {podKey?.substring(0, POD_KEYS_COUNT / 2)}
          <br />
          {podKey?.substring(POD_KEYS_COUNT / 2, POD_KEYS_COUNT)}
        </Typography>
      </Box>
    </>
  );
}
